import { useEffect, useState, useRef } from 'react';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import ProUpsell from '@/components/ProUpsell';
import Seo from '@/components/Seo';
import { toast, Toaster } from 'sonner';
import { Camera, Trash2, Paperclip, Receipt as ReceiptIcon, Loader2, X } from 'lucide-react';

const gbp = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const CATS = ['materials', 'plant', 'fuel', 'subcontractor', 'overheads', 'other'];

export default function ReceiptsPage() {
  const { user } = useAuth();
  const isPro = user?.is_pro || user?.role === 'admin';
  const [receipts, setReceipts] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [bulkJobId, setBulkJobId] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [r, j] = await Promise.all([api.get('/receipts'), api.get('/jobs')]);
      setReceipts(r.data); setJobs(j.data);
    } catch (e) {
      if (e.response?.status !== 402) toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { if (isPro) load(); else setLoading(false); }, [isPro]);

  if (!isPro) return <ProUpsell feature="Receipt scanning" testIdSuffix="receipts" tier="pro" />;

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setScanning(true);
    let ok = 0, failed = 0;
    for (let i = 0; i < files.length; i++) {
      setScanProgress(files.length > 1 ? `Scanning ${i + 1} of ${files.length}…` : '');
      try {
        const fd = new FormData();
        fd.append('file', files[i]);
        const r = await api.post('/receipts', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setReceipts((prev) => [r.data, ...prev]);
        ok += 1;
      } catch (err) {
        failed += 1;
      }
    }
    setScanning(false);
    setScanProgress('');
    if (fileRef.current) fileRef.current.value = '';
    if (ok > 0) toast.success(`${ok} receipt${ok === 1 ? '' : 's'} scanned — check the figures.`);
    if (failed > 0) toast.error(`${failed} couldn't be read — please try again.`);
  };

  const patch = async (id, fields) => {
    setReceipts((prev) => prev.map((r) => (r.id === id ? { ...r, ...fields } : r)));
    try { await api.patch(`/receipts/${id}`, fields); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    const rec = receipts.find((r) => r.id === id);
    const msg = rec?.attached ? 'Delete this receipt? This also removes its cost from the job.' : 'Delete this receipt?';
    if (!window.confirm(msg)) return;
    try { await api.delete(`/receipts/${id}`); setReceipts((prev) => prev.filter((r) => r.id !== id)); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const attach = async (id, jobId) => {
    if (!jobId) return;
    try { await api.post(`/receipts/${id}/attach`, { job_id: jobId }); toast.success('Added to job as a cost'); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const bulkAttach = async () => {
    if (!bulkJobId || selected.size === 0) return;
    setBulkBusy(true);
    let ok = 0;
    for (const id of selected) {
      try { await api.post(`/receipts/${id}/attach`, { job_id: bulkJobId }); ok += 1; } catch (e) { /* keep going */ }
    }
    setBulkBusy(false);
    setSelected(new Set());
    setBulkJobId('');
    if (ok > 0) toast.success(`${ok} receipt${ok === 1 ? '' : 's'} attached to the job`);
    load();
  };

  const src = (p) => `${api.defaults.baseURL}/receipts/file/${p}`;
  const inbox = receipts.filter((r) => !r.attached);
  const done = receipts.filter((r) => r.attached);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo title="Receipts" noindex />
      <Toaster position="top-center" richColors />
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-widest text-brand font-semibold">Snap · scan · reclaim</p>
          <h1 className="font-display font-semibold text-4xl sm:text-5xl uppercase tracking-tight mt-1">Receipts</h1>
          <p className="text-slate-600 mt-2">Photograph a receipt — AI reads the supplier, total and VAT. Review, then attach to a job.</p>
        </div>
        <div>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onUpload} data-testid="receipt-upload-input" />
          <button onClick={() => fileRef.current?.click()} disabled={scanning} className="tm-btn" data-testid="receipt-scan-btn">
            {scanning ? <><Loader2 size={16} className="animate-spin"/> {scanProgress || 'Scanning…'}</> : <><Camera size={16}/> Scan receipts</>}
          </button>
        </div>
      </div>

      {loading ? <p className="font-mono text-slate-400">Loading…</p> : (
        <>
          {inbox.length === 0 && done.length === 0 ? (
            <div className="tm-card p-10 text-center" data-testid="receipts-empty">
              <ReceiptIcon size={40} className="mx-auto text-slate-300 mb-3" />
              <h3 className="font-display font-bold text-lg uppercase">No receipts yet</h3>
              <p className="text-slate-500 mt-1">Tap “Scan a receipt” to snap your first one.</p>
            </div>
          ) : (
            <>
              {inbox.length > 0 && (
                <>
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                    <h2 className="font-display font-semibold text-lg uppercase tracking-tight">To review ({inbox.length})</h2>
                    {selected.size > 0 && (
                      <div className="flex items-center gap-2 bg-slate-900 text-white px-3 py-2 rounded" data-testid="bulk-attach-bar">
                        <span className="text-xs uppercase tracking-wide whitespace-nowrap font-semibold">{selected.size} selected →</span>
                        <select className="tm-input bg-white text-slate-900 text-sm min-h-0 py-1.5" value={bulkJobId} onChange={(e) => setBulkJobId(e.target.value)} data-testid="bulk-attach-job-select">
                          <option value="">Attach all to job…</option>
                          {jobs.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
                        </select>
                        <button onClick={bulkAttach} disabled={!bulkJobId || bulkBusy} className="tm-btn text-xs min-h-0 py-1.5" data-testid="bulk-attach-btn">
                          {bulkBusy ? <><Loader2 size={14} className="animate-spin"/> Attaching…</> : <><Paperclip size={14}/> Attach</>}
                        </button>
                        <button onClick={() => setSelected(new Set())} className="text-slate-300 hover:text-white px-1" title="Clear selection"><X size={16}/></button>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10" data-testid="receipts-inbox">
                    {inbox.map((r) => (
                      <ReceiptCard key={r.id} r={r} src={src} jobs={jobs} onPatch={patch} onDelete={del} onAttach={attach}
                        selected={selected.has(r.id)} onToggleSelect={() => toggleSelect(r.id)} />
                    ))}
                  </div>
                </>
              )}
              {done.length > 0 && (
                <>
                  <h2 className="font-display font-semibold text-lg uppercase tracking-tight mb-4 text-slate-500">Attached to jobs ({done.length})</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="receipts-attached">
                    {done.map((r) => {
                      const job = jobs.find((j) => j.id === r.job_id);
                      return (
                        <ReceiptCard key={r.id} r={r} src={src} jobs={jobs} onPatch={patch} onDelete={del} attached jobName={job ? job.name : 'Attached'} />
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function ReceiptCard({ r, src, jobs, onPatch, onDelete, onAttach, attached, jobName, selected, onToggleSelect }) {
  const [jobId, setJobId] = useState('');
  const [local, setLocal] = useState({ supplier: r.supplier || '', amount: r.amount ?? 0, vat_amount: r.vat_amount ?? 0, date: r.date || '', category: r.category || 'materials' });
  const setL = (k, v) => setLocal((s) => ({ ...s, [k]: v }));
  const saveField = (k, v) => { if (v !== r[k]) onPatch(r.id, { [k]: v }); };
  return (
    <div className={`tm-card p-4 ${selected ? 'ring-2 ring-brand' : ''}`} data-testid={`receipt-card-${r.id}`}>
      <div className="flex gap-4">
        <div className="shrink-0 relative">
          {!attached && (
            <input type="checkbox" checked={!!selected} onChange={onToggleSelect} data-testid={`receipt-select-${r.id}`}
              className="absolute top-1 left-1 w-5 h-5 accent-brand cursor-pointer z-10" title="Select for bulk attach" />
          )}
          <a href={src(r.storage_path)} target="_blank" rel="noreferrer">
            <img src={src(r.storage_path)} alt="receipt" className="w-24 h-24 object-cover border border-slate-300" />
          </a>
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <input className="tm-input" value={local.supplier} placeholder="Supplier" onChange={(e) => setL('supplier', e.target.value)} onBlur={(e) => saveField('supplier', e.target.value)} data-testid={`receipt-supplier-${r.id}`} />
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="tm-label">Gross (£)</span>
              <input type="number" className="tm-input" value={local.amount} onChange={(e) => setL('amount', e.target.value)} onBlur={(e) => saveField('amount', parseFloat(e.target.value) || 0)} data-testid={`receipt-amount-${r.id}`} /></label>
            <label className="block"><span className="tm-label">VAT (£)</span>
              <input type="number" className="tm-input" value={local.vat_amount} onChange={(e) => setL('vat_amount', e.target.value)} onBlur={(e) => saveField('vat_amount', parseFloat(e.target.value) || 0)} data-testid={`receipt-vat-${r.id}`} /></label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="tm-label">Date</span>
              <input type="date" className="tm-input" value={local.date} onChange={(e) => { setL('date', e.target.value); saveField('date', e.target.value); }} /></label>
            <label className="block"><span className="tm-label">Category</span>
              <select className="tm-input bg-white" value={local.category} onChange={(e) => { setL('category', e.target.value); saveField('category', e.target.value); }}>
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select></label>
          </div>
        </div>
      </div>
      {r.confidence > 0 && r.confidence < 0.6 && (
        <p className="text-[11px] text-amber-600 mt-2">⚠ Low confidence scan — please double-check the figures.</p>
      )}
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-200">
        {attached ? (
          <>
            <span className="flex-1 min-w-0 text-xs font-mono text-emerald-600 truncate" data-testid={`receipt-attached-badge-${r.id}`}>
              <Paperclip size={12} className="inline mr-1"/> {jobName || 'Attached'} · edits update the job cost
            </span>
            <button onClick={() => onDelete(r.id)} className="text-slate-400 hover:text-rose-600 px-2" title="Remove receipt & its job cost"><Trash2 size={16}/></button>
          </>
        ) : (
          <>
            <select className="tm-input bg-white flex-1 text-sm" value={jobId} onChange={(e) => setJobId(e.target.value)} data-testid={`receipt-job-select-${r.id}`}>
              <option value="">Attach to job…</option>
              {jobs.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
            </select>
            <button onClick={() => onAttach(r.id, jobId)} disabled={!jobId} className="tm-btn text-xs min-h-0 py-2" data-testid={`receipt-attach-${r.id}`}><Paperclip size={14}/> Attach</button>
            <button onClick={() => onDelete(r.id)} className="text-slate-400 hover:text-rose-600 px-2"><Trash2 size={16}/></button>
          </>
        )}
      </div>
    </div>
  );
}
